import { Component } from '@theme/component';
import { debounce } from './utilities.js';
import { CartNotification } from './cart-notification.js';

/**
 * APC American Eagle Header Component
 * Manages megamenu interactions with hover behavior and mobile menu
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} menuitem - Desktop menu items with megamenu dropdown
 * @property {HTMLElement[]} megamenusWrapper - Desktop megamenu wrappers (one per brand)
 * @property {HTMLElement[]} megamenu - Desktop megamenu dropdown
 * @property {HTMLElement[]} menuToggle - Mobile menu close button and backdrop
 * @property {HTMLElement} mobileMenu - Mobile menu element
 * @property {HTMLElement} panelsContainer - Mobile panels container
 * @property {HTMLElement} desktopMenu - Desktop menu element
 * @property {HTMLElement} headerActions - Header actions bar
 * @property {HTMLElement} headerMain - Header main bar
 *
 * @extends {Component<Refs>}
 */

class APCAEHeader extends Component {
  /** @type {HTMLElement | null} */
  #activeMenuItem = null;

  /** @type {ReturnType<typeof debounce> | null} */
  #debouncedCloseMegamenu = null;

  /** @type {boolean} */
  #mobileMenuOpen = false;

  /** @type {string | null} */
  #currentPanelId = null;

  /** @type {string} */
  #activeBrand = 'american-eagle';

  /** @type {boolean} */
  #megamenuOpen = false;

  /** @type {((e: KeyboardEvent) => void) | null} */
  #focusTrapHandler = null;

  connectedCallback() {
    super.connectedCallback();

    // Create debounced close megamenu function
    this.#debouncedCloseMegamenu = debounce(() => {
      if (this.#activeMenuItem) {
        this.#closeMegamenu(this.#activeMenuItem);
        this.#activeMenuItem = null;
      }
    }, 200);

    this.#setupMobileMenu();
    this.#setupDesktopKeyboardNavigation();
    this.#calculateMegamenuHeights();
    this.#initializeBrandState();
    this.#setupStickyPosition();
  }

  /**
   * Initialize brand state from panels container or desktop menu data attribute
   */
  #initializeBrandState() {
    const { panelsContainer, desktopMenu } = this.refs;

    // Get initial brand from mobile panels container or desktop menu
    const initialBrand =
      panelsContainer?.getAttribute('data-active-brand') ||
      desktopMenu?.getAttribute('data-active-brand') ||
      'american-eagle';

    this.#activeBrand = initialBrand;
    this.#currentPanelId = `mobile-panel-${initialBrand}-main`;
  }

  /**
   * Setup sticky position by measuring announcement bar height
   * Sets CSS variable used by the header section's sticky top offset
   */
  #setupStickyPosition() {
    const announcementBar = document.querySelector('.shopify-section--apc-announcement-bar');

    const updateHeight = () => {
      const announcementBarHeight = announcementBar?.getBoundingClientRect().height ?? 0;
      const headerHeight = this.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--announcement-bar-height', `${announcementBarHeight}px`);
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    };

    // Initial values are set by inline script in apc-header.liquid to prevent layout jump.
    // ResizeObservers keep values updated when elements resize dynamically.
    if (announcementBar) new ResizeObserver(updateHeight).observe(announcementBar);
    new ResizeObserver(updateHeight).observe(this);
  }

  /**
   * Get the megamenu wrapper for the active brand
   * @returns {HTMLElement | null}
   */
  #getActiveMegamenusWrapper() {
    const { megamenusWrapper } = this.refs;
    if (!megamenusWrapper) return null;

    const wrappers = Array.isArray(megamenusWrapper) ? megamenusWrapper : [megamenusWrapper];
    return wrappers.find((wrapper) => wrapper.getAttribute('data-brand') === this.#activeBrand) || null;
  }

  /**
   * Enable scroll lock
   */
  #enableScrollLock() {
    document.documentElement.setAttribute('scroll-lock', '');
  }

  /**
   * Disable scroll lock
   */
  #disableScrollLock() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Show backdrop (for desktop megamenu)
   */
  #showBackdrop() {
    const { menuToggle } = this.refs;
    if (menuToggle) {
      menuToggle.forEach((toggle) => {
        if (toggle.hasAttribute('data-backdrop')) {
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    }
  }

  /**
   * Hide backdrop (for desktop megamenu)
   */
  #hideBackdrop() {
    const { menuToggle } = this.refs;
    if (menuToggle) {
      menuToggle.forEach((toggle) => {
        if (toggle.hasAttribute('data-backdrop')) {
          toggle.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  /**
   * Setup mobile menu ESC key handler
   */
  #setupMobileMenu() {
    // ESC key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#mobileMenuOpen) {
        this.toggleMobileMenu(e);
      }
    });
  }

  /**
   * Setup desktop keyboard navigation for megamenu
   * - Escape to close megamenu
   * - Enter to open megamenu (if has submenu) or navigate (if no submenu)
   * - Space to toggle megamenu (only for items with submenu)
   */
  #setupDesktopKeyboardNavigation() {
    const isDesktop = () => window.innerWidth >= 1024;

    // ESC key handler for desktop megamenu
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#megamenuOpen && isDesktop()) {
        e.preventDefault();
        // Remove focus trap before closing
        const activeMegamenuId = this.#activeMenuItem?.getAttribute('aria-controls');
        if (activeMegamenuId) {
          this.#removeFocusTrap(document.getElementById(activeMegamenuId));
        }
        if (this.#activeMenuItem) {
          this.#activeMenuItem.focus();
          this.#closeMegamenu(this.#activeMenuItem);
          this.#activeMenuItem = null;
        }
      }
    });

    // Target ALL menu links, not just ones with submenus
    /** @type {NodeListOf<HTMLElement>} */
    const menuLinks = this.querySelectorAll('.apc-desktop-menu-brand__navigation-item');

    menuLinks.forEach((link) => {
      link.addEventListener('keydown', (e) => {
        if (!isDesktop()) return;

        const hasSubmenu = link.hasAttribute('aria-controls');

        // Enter: open megamenu if has submenu, otherwise let default navigation happen
        // Space: only toggle megamenu for links with submenus
        const shouldToggle = (e.key === 'Enter' && hasSubmenu) || (e.key === ' ' && hasSubmenu);

        if (!shouldToggle) return;

        e.preventDefault();

        if (this.#activeMenuItem === link) {
          this.#closeViaKeyboard(link);
        } else {
          this.#activateViaKeyboard(link);
        }
      });
    });
  }

  /**
   * Close megamenu via keyboard and clean up focus trap
   * @param {HTMLElement} menuLink - The menu link element
   */
  #closeViaKeyboard(menuLink) {
    const megamenuId = menuLink.getAttribute('aria-controls');
    if (megamenuId) {
      this.#removeFocusTrap(document.getElementById(megamenuId));
    }
    this.#closeMegamenu(menuLink);
    this.#activeMenuItem = null;
  }

  /**
   * Activate megamenu via keyboard (similar to activate but for keyboard)
   * @param {HTMLElement} menuLink - The menu link element
   */
  #activateViaKeyboard(menuLink) {
    // Cancel any pending close
    if (this.#debouncedCloseMegamenu) {
      this.#debouncedCloseMegamenu.cancel();
    }

    // Close cart notification when opening megamenu
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification instanceof CartNotification) cartNotification.close(true);

    const megamenuId = menuLink.getAttribute('aria-controls');
    if (!megamenuId) return;

    const megamenu = document.getElementById(megamenuId);
    if (!megamenu) return;

    const wasAnyActive = this.#megamenuOpen;

    // Close previously active megamenu content
    if (this.#activeMenuItem && this.#activeMenuItem !== menuLink) {
      const prevMegamenuId = this.#activeMenuItem.getAttribute('aria-controls');
      if (prevMegamenuId) {
        const prevMegamenu = document.getElementById(prevMegamenuId);
        if (prevMegamenu) {
          prevMegamenu.setAttribute('aria-expanded', 'false');
        }
      }
      this.#activeMenuItem.setAttribute('aria-expanded', 'false');
    }

    // Activate new megamenu
    this.#activeMenuItem = menuLink;
    menuLink.setAttribute('aria-expanded', 'true');
    megamenu.setAttribute('aria-expanded', 'true');

    // Update wrapper height to match new megamenu content
    this.#updateMegamenuHeight(megamenu);

    // Open wrapper if this is the first megamenu opening
    if (!wasAnyActive) {
      this.#megamenuOpen = true;
      const megamenusWrapper = this.#getActiveMegamenusWrapper();
      if (megamenusWrapper) {
        megamenusWrapper.setAttribute('data-expanded', 'true');
      }
      this.#enableScrollLock();
      this.#showBackdrop();
    }

    // Add focus trap for keyboard navigation
    this.#trapFocusInMegamenu(megamenu);

    // Move focus to first focusable element in megamenu
    // CSS has 200ms delay on visibility transition, so we need to wait for it
    const firstFocusable = megamenu.querySelector('a, button, [tabindex="0"]');
    if (firstFocusable instanceof HTMLElement) {
      setTimeout(() => firstFocusable.focus(), 250);
    }
  }

  /**
   * Trap focus inside megamenu when navigating via keyboard
   * @param {HTMLElement} megamenu - The megamenu element
   */
  #trapFocusInMegamenu(megamenu) {
    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = megamenu.querySelectorAll(focusableSelector);

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.#focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab: if on first element, go to last
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          if (lastFocusable instanceof HTMLElement) {
            lastFocusable.focus();
          }
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          if (firstFocusable instanceof HTMLElement) {
            firstFocusable.focus();
          }
        }
      }
    };

    megamenu.addEventListener('keydown', this.#focusTrapHandler);
  }

  /**
   * Remove focus trap from megamenu
   * @param {HTMLElement | null} megamenu - The megamenu element
   */
  #removeFocusTrap(megamenu) {
    if (this.#focusTrapHandler && megamenu) {
      megamenu.removeEventListener('keydown', this.#focusTrapHandler);
      this.#focusTrapHandler = null;
    }
  }

  /**
   * Recalculate megamenu heights (clears existing values first)
   */
  #recalculateMegamenuHeights() {
    if (window.innerWidth < 1024) return;
    const { megamenu: megamenus } = this.refs;
    if (!megamenus) return;

    // Clear existing CSS variables to force recalculation
    megamenus.forEach((menu) => {
      menu.style.removeProperty('--content-height');
    });

    this.#calculateMegamenuHeights();
  }

  /**
   * Toggle mobile menu open/closed
   * @param {Event} event
   */
  toggleMobileMenu(event) {
    event.preventDefault();
    if (this.#mobileMenuOpen) {
      this.#closeMobileMenu();
    } else {
      this.#openMobileMenu();
    }
  }

  /**
   * Open mobile menu
   */
  #openMobileMenu() {
    this.#mobileMenuOpen = true;

    const { mobileMenu, menuToggle } = this.refs;

    if (!mobileMenu) return;

    // Update data-expanded to trigger animation
    mobileMenu.setAttribute('data-expanded', 'true');
    if (menuToggle) {
      menuToggle.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
    }

    // Enable scroll lock
    this.#enableScrollLock();
  }

  /**
   * Close mobile menu
   */
  #closeMobileMenu() {
    this.#mobileMenuOpen = false;

    const { mobileMenu, menuToggle } = this.refs;
    if (!mobileMenu) return;

    // Update data-expanded
    mobileMenu.setAttribute('data-expanded', 'false');
    if (menuToggle) {
      menuToggle.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    }

    // Reset to main panel of current brand
    const mainPanelId = `mobile-panel-${this.#activeBrand}-main`;
    this.#switchPanel(mainPanelId);

    // Disable scroll lock
    this.#disableScrollLock();
  }

  /**
   * Navigate to a sub-panel
   * @param {Event} event
   */
  navigateToPanel(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const trigger = event.target.closest('[ref="panelTrigger[]"]');

    if (!trigger) return;

    const panelId = trigger.getAttribute('aria-controls');

    if (!panelId) return;

    this.#switchPanel(panelId);
  }

  /**
   * Navigate back to main panel
   * @param {Event} event
   */
  navigateBackToMain(event) {
    event.preventDefault();
    const mainPanelId = `mobile-panel-${this.#activeBrand}-main`;
    this.#switchPanel(mainPanelId);
  }

  /**
   * Switch between brands in mobile menu
   * @param {Event} event
   */
  switchBrand(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const button = event.target.closest('[data-brand]');
    if (!button) return;

    const newBrand = button.getAttribute('data-brand');
    if (!newBrand || newBrand === this.#activeBrand) return;

    const { brandPanels, brandButton, panelsContainer } = this.refs;
    if (!brandPanels || !panelsContainer) return;

    // Update active brand
    this.#activeBrand = newBrand;
    panelsContainer.setAttribute('data-active-brand', newBrand);

    // Switch visible brand panels
    const panels = Array.isArray(brandPanels) ? brandPanels : [brandPanels];
    panels.forEach((panel) => {
      const panelBrand = panel.getAttribute('data-brand');
      if (panelBrand === newBrand) {
        panel.classList.add('apc-mobile-menu__brand-panels--active');
      } else {
        panel.classList.remove('apc-mobile-menu__brand-panels--active');
      }
    });

    // Update button states
    if (brandButton) {
      const buttons = Array.isArray(brandButton) ? brandButton : [brandButton];
      buttons.forEach((btn) => {
        const btnBrand = btn.getAttribute('data-brand');
        const isActive = btnBrand === newBrand;
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.classList.toggle('apc-brand-selector__brand--active', isActive);
      });
    }

    // Reset to main panel of the new brand
    const newMainPanelId = `mobile-panel-${newBrand}-main`;
    this.#currentPanelId = newMainPanelId;

    // Update mobile menu color scheme
    const { mobileMenu } = this.refs;
    if (mobileMenu) {
      const aeColorScheme = mobileMenu.getAttribute('data-color-scheme-ae');
      const aerieColorScheme = mobileMenu.getAttribute('data-color-scheme-aerie');
      const newColorScheme = newBrand === 'aerie' ? aerieColorScheme : aeColorScheme;

      // Remove existing color scheme classes and add new one
      const currentClasses = mobileMenu.className.split(' ');
      const filteredClasses = currentClasses.filter((cls) => !cls.startsWith('color-'));
      filteredClasses.push(`color-${newColorScheme}`);
      mobileMenu.className = filteredClasses.join(' ');
    }

    // Reset all panels in new brand to correct state
    const newBrandContainer = panels.find((p) => p.getAttribute('data-brand') === newBrand);
    if (newBrandContainer) {
      const allPanels = newBrandContainer.querySelectorAll('.apc-mobile-menu-brand__panel');
      allPanels.forEach((panel) => {
        const isMainPanel = panel.id === newMainPanelId;
        panel.setAttribute('aria-hidden', isMainPanel ? 'false' : 'true');
      });
    }
  }

  /**
   * Switch between panels with animation
   * @param {string} targetPanelId - ID of the panel to switch to
   */
  #switchPanel(targetPanelId) {
    if (!this.#currentPanelId) return;

    const currentPanel = document.getElementById(this.#currentPanelId);
    const targetPanel = document.getElementById(targetPanelId);
    const { panelsContainer } = this.refs;

    if (!targetPanel || !panelsContainer) return;

    // Check if going to main panel (brand-specific)
    const mainPanelId = `mobile-panel-${this.#activeBrand}-main`;
    const isGoingToMain = targetPanelId === mainPanelId;

    // Set transition type on wrapper for CSS to handle animations
    panelsContainer.setAttribute('data-transition', isGoingToMain ? 'to-main' : 'to-sub');

    // Switch panels - CSS handles all animation timing with delays
    if (currentPanel) {
      currentPanel.setAttribute('aria-hidden', 'true');
    }
    targetPanel.setAttribute('aria-hidden', 'false');
    this.#currentPanelId = targetPanelId;
    this.#updatePanelTriggerStates(targetPanelId);

    // Move focus to target panel to avoid aria-hidden conflict
    const focusTarget = targetPanel.querySelector('button, a, [tabindex="0"]');
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus();
    }
  }

  /**
   * Update panel trigger button states
   * @param {string} activePanelId
   */
  #updatePanelTriggerStates(activePanelId) {
    const { panelTrigger } = this.refs;
    if (!panelTrigger) return;

    const triggers = Array.isArray(panelTrigger) ? panelTrigger : [panelTrigger];
    triggers.forEach((trigger) => {
      const controlsId = trigger.getAttribute('aria-controls');
      trigger.setAttribute('aria-expanded', controlsId === activePanelId ? 'true' : 'false');
    });
  }

  /**
   * Activate a menu item and show its megamenu
   * @param {PointerEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    // Find the menuitem link within the hovered menu item (li)
    const menuLink = event.target.querySelector('[ref="menuitem"]');
    if (!menuLink || !(menuLink instanceof HTMLElement)) return;

    // Always cancel pending close when hovering any menu item
    if (this.#debouncedCloseMegamenu) {
      this.#debouncedCloseMegamenu.cancel();
    }

    // If hovering the same menu item that's already active, just keep it open
    if (this.#activeMenuItem === menuLink) {
      return;
    }

    // Close cart notification when opening megamenu
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification instanceof CartNotification) cartNotification.close(true);

    const megamenuId = menuLink.getAttribute('aria-controls');
    if (!megamenuId) return;

    const megamenu = document.getElementById(megamenuId);
    if (!megamenu) return;

    const wasAnyActive = this.#megamenuOpen;

    // Close previously active megamenu and fold container before opening new one
    if (this.#activeMenuItem && this.#activeMenuItem !== menuLink) {
      const prevMegamenuId = this.#activeMenuItem.getAttribute('aria-controls');
      if (prevMegamenuId) {
        const prevMegamenu = document.getElementById(prevMegamenuId);
        if (prevMegamenu) {
          const megamenusWrapper = this.#getActiveMegamenusWrapper();
          const megamenusContainer = megamenusWrapper?.closest('.apc-desktop-menu-brand__megamenus-container');

          if (megamenusWrapper && megamenusContainer) {
            // Close the previous megamenu content
            this.#activeMenuItem.setAttribute('aria-expanded', 'false');
            prevMegamenu.setAttribute('aria-expanded', 'false');

            // Fold the container (close wrapper)
            megamenusWrapper.setAttribute('data-expanded', 'false');
            this.#activeMenuItem = menuLink;

            // Wait for grid-template-rows transition to complete
            const onContainerTransitionEnd = (/** @type {TransitionEvent} */ e) => {
              if (e.propertyName !== 'grid-template-rows') return;

              megamenusContainer.removeEventListener('transitionend', onContainerTransitionEnd);

              // Only open if this menu item is still the target (user hasn't moved to another)
              if (this.#activeMenuItem === menuLink) {
                // Open new megamenu content
                menuLink.setAttribute('aria-expanded', 'true');
                megamenu.setAttribute('aria-expanded', 'true');

                // Unfold the container (open wrapper)
                megamenusWrapper.setAttribute('data-expanded', 'true');

                // Update wrapper height to match new megamenu content
                if (window.innerWidth >= 1024) {
                  this.#updateMegamenuHeight(megamenu);
                }
              }
            };

            megamenusContainer.addEventListener('transitionend', onContainerTransitionEnd);

            return;
          }
        }
      }
      this.#activeMenuItem.setAttribute('aria-expanded', 'false');
    }

    // Activate new megamenu content (no previous megamenu was open)
    this.#activeMenuItem = menuLink;
    menuLink.setAttribute('aria-expanded', 'true');
    megamenu.setAttribute('aria-expanded', 'true');

    // Update wrapper height to match new megamenu content
    if (window.innerWidth >= 1024) {
      this.#updateMegamenuHeight(megamenu);
    }

    // Open wrapper if this is the first megamenu opening
    if (!wasAnyActive) {
      this.#megamenuOpen = true;
      const megamenusWrapper = this.#getActiveMegamenusWrapper();
      if (megamenusWrapper) {
        megamenusWrapper.setAttribute('data-expanded', 'true');
      }

      this.#enableScrollLock();
      this.#showBackdrop();
    }
  };

  /**
   * Keep the megamenu active when hovering over it
   */
  keepActive = () => {
    // Cancel any pending debounced close when entering megamenu
    if (this.#debouncedCloseMegamenu) {
      this.#debouncedCloseMegamenu.cancel();
    }
  };

  /**
   * Deactivate a menu item and hide its megamenu
   * @param {PointerEvent} event
   */
  deactivate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    // Debounced close with 200ms delay
    if (this.#debouncedCloseMegamenu) {
      this.#debouncedCloseMegamenu();
    }
  };

  /**
   * Calculate and store heights for all megamenus on load
   * Measurements are done safely while megamenus are hidden
   * Heights persist for current page session only (recalculated on each page load)
   */
  #calculateMegamenuHeights() {
    // only on desktop
    if (window.innerWidth < 1024) return;
    const { megamenu: megamenus, megamenusWrapper } = this.refs;
    if (!megamenus || !megamenusWrapper) return;

    const wrappers = Array.isArray(megamenusWrapper) ? megamenusWrapper : [megamenusWrapper];

    // Skip if already calculated (first megamenu has the CSS variable)
    if (megamenus[0]?.style.getPropertyValue('--content-height')) return;

    // Ensure all wrappers are non-interactive during measurement
    wrappers.forEach((wrapper) => {
      wrapper.style.pointerEvents = 'none';
      wrapper.style.visibility = 'hidden';
    });

    // Collect all measurements in one batch to minimize reflows
    const measurements = megamenus.map((menu) => {
      // Store original inline styles
      const originalPosition = menu.style.position;
      const originalInset = menu.style.inset;

      // Temporarily make it flow content to measure true height
      menu.style.position = 'relative';
      menu.style.inset = 'auto';

      return { menu, originalPosition, originalInset };
    });

    // Force single reflow after all style changes
    wrappers[0]?.offsetHeight;

    // Read all measurements
    measurements.forEach(({ menu, originalPosition, originalInset }) => {
      const contentHeight = menu.scrollHeight;
      menu.style.setProperty('--content-height', `${contentHeight}px`);

      // Restore original styles
      menu.style.position = originalPosition;
      menu.style.inset = originalInset;
    });

    // Restore wrapper interaction
    wrappers.forEach((wrapper) => {
      wrapper.style.pointerEvents = '';
      wrapper.style.visibility = '';
    });
  }

  /**
   * Update megamenu wrapper height based on active megamenu content
   * @param {HTMLElement} megamenu - The active megamenu element
   */
  #updateMegamenuHeight(megamenu) {
    const megamenusWrapper = this.#getActiveMegamenusWrapper();
    if (!megamenusWrapper) return;

    // Get the pre-calculated height from CSS variable
    const contentHeight = megamenu.style.getPropertyValue('--content-height');
    if (contentHeight) {
      megamenusWrapper.style.maxHeight = contentHeight;
    }
  }

  /**
   * Close a megamenu
   * @param {HTMLElement} menuLink - The menu link element
   */
  #closeMegamenu(menuLink) {
    const megamenuId = menuLink.getAttribute('aria-controls');
    if (!megamenuId) return;

    const megamenu = document.getElementById(megamenuId);
    if (!megamenu) return;

    // Close the content panel
    menuLink.setAttribute('aria-expanded', 'false');
    megamenu.setAttribute('aria-expanded', 'false');

    // Close wrapper when all megamenus are closed
    this.#megamenuOpen = false;
    const megamenusWrapper = this.#getActiveMegamenusWrapper();
    if (megamenusWrapper) {
      megamenusWrapper.setAttribute('data-expanded', 'false');
      // Don't reset max-height immediately - let the grid animation handle closing
      // The max-height will be set again when opening a new megamenu
    }

    // Disable scroll lock and hide backdrop
    this.#disableScrollLock();
    this.#hideBackdrop();
  }
}

if (!customElements.get('apc-header')) {
  customElements.define('apc-header', APCAEHeader);
}
